import React, { useState, useMemo, useEffect } from "react";
import { useParams } from "react-router-dom";
import { Row, Col, Card, Form, Spinner } from "react-bootstrap";
import { useGetDossierPartageQuery, useGetDossierByIdQuery } from "actions/dossiers/queries";
import { 
  useAddDossierPartageMutation, 
  useDeleteDossierPartageMutation,
  useUpdateDossierPartageMutation
} from "actions/dossiers/mutations";
import { RiAddLine, RiInformationLine, RiLockPasswordLine } from "react-icons/ri";
import { FiEdit } from "react-icons/fi";
import HeaderFirst from "utils/HeaderFirst";
import CustomBootstrapTable from "utils/reactTable";
import TablePaginators from "components/Pagination/TablePaginator";
import ConfirmDeleteModal from "components/ConfirmDeleteModal";
import { toast } from "react-toastify";
import { toastApiErrors } from "utils/toastApiError";
import searchIcon from "../../image/search.png";
import supprimerIcon from "../../image/trashs.png";

const formatDate = (dateString) => {
  if (!dateString) return "-";
  try {
    return new Intl.DateTimeFormat("fr-FR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric"
    }).format(new Date(dateString));
  } catch (e) {
    return dateString;
  }
};

export default function PartageDossier({ hideBack = false, embedded = false }) {
  const { clientID: dossier_id } = useParams();
  const { data: dossier, isLoading: isDossierLoading } = useGetDossierByIdQuery({ id: dossier_id }, { skip: !dossier_id });
  const { data: sharesResponse, isLoading: isSharesLoading, refetch } = useGetDossierPartageQuery({ dossier_id }, { skip: dossier?.user_role !== 'ADMIN' });
  const [addPartage, { isLoading: isAdding }] = useAddDossierPartageMutation();
  const [deletePartage, { isLoading: isDeleting }] = useDeleteDossierPartageMutation();
  const [updatePartage, { isLoading: isUpdating }] = useUpdateDossierPartageMutation();

  const [email, setEmail] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [role, setRole] = useState("EDITEUR");
  const [searchTerm, setSearchTerm] = useState("");

  const [pageSize, setPageSize] = useState(10);
  const [paginationIndex, setPaginationIndex] = useState(0);
  const [selectedRows, setSelectedRows] = useState([]);
  const [editingAccess, setEditingAccess] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const [sharesState, setSharesState] = useState([]);

  // Garder les donnees precedentes pendant le refetch pour eviter le flash de liste vide
  useEffect(() => {
    if (sharesResponse?.data) {
      setSharesState(sharesResponse.data);
    }
  }, [sharesResponse]);

  const shares = sharesState;
  const isAuthorized = dossier?.user_role === 'ADMIN';

  // Pré-remplir le formulaire lors du passage en mode modification
  useEffect(() => {
    if (editingAccess) {
      setEmail(editingAccess.email || "");
      setFirstName(editingAccess.first_name || "");
      setLastName(editingAccess.last_name || "");
      setRole(editingAccess.role || "EDITEUR");
    } else {
      setEmail("");
      setFirstName("");
      setLastName("");
      setRole("EDITEUR");
    }
  }, [editingAccess]);

  const filteredShares = useMemo(() => {
    return shares.filter((share) => {
      const name = `${share.first_name || ""} ${share.last_name || ""}`.toLowerCase();
      return (
        name.includes(searchTerm.toLowerCase()) ||
        share.email?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    });
  }, [shares, searchTerm]);

  const currentPageData = useMemo(() => {
    return filteredShares.slice(paginationIndex * pageSize, (paginationIndex + 1) * pageSize);
  }, [filteredShares, paginationIndex, pageSize]);

  const handlePageChange = (newIndex) => {
    setPaginationIndex(newIndex);
  };

  const handleRowSelect = (row, isSelect) => {
    if (isSelect) {
      setSelectedRows((prev) => [...prev, row.id]);
    } else {
      setSelectedRows((prev) => prev.filter((id) => id !== row.id));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email) {
      toast.error("Veuillez saisir une adresse email.");
      return;
    }

    if (editingAccess) {
      // Modification de l'accès existant
      try {
        const response = await updatePartage({
          dossier_id,
          access_id: editingAccess.id,
          body: {
            role,
            first_name: firstName,
            last_name: lastName
          }
        }).unwrap();

        if (response.status === "success") {
          toast.success(response.message || "Accès mis à jour avec succès !");
          setEditingAccess(null);
          setSelectedRows([]);
          refetch();
        } else {
          toast.error(response.message || "Une erreur est survenue.");
        }
      } catch (err) {
        toastApiErrors(err);
      }
    } else {
      // Ajout d'un nouvel accès
      try {
        const response = await addPartage({
          dossier_id,
          body: {
            email,
            role,
            first_name: firstName,
            last_name: lastName
          }
        }).unwrap();

        if (response.status === "success") {
          toast.success(response.message || "Accès partagé avec succès !");
          setEmail("");
          setFirstName("");
          setLastName("");
          setRole("EDITEUR");
          setPaginationIndex(0);
          refetch();
        } else {
          toast.error(response.message || "Une erreur est survenue.");
        }
      } catch (err) {
        toastApiErrors(err);
      }
    }
  };

  const onClickEdit = () => {
    if (selectedRows.length !== 1) return;
    const target = shares.find((s) => s.id === selectedRows[0]);
    if (target) {
      setEditingAccess(target);
    }
  };

  const onClickDeleteBulk = () => {
    if (selectedRows.length === 0) {
      toast.warning("Veuillez sélectionner au moins un accès à supprimer.");
      return;
    }
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = async () => {
    try {
      const deletePromises = selectedRows.map((id) =>
        deletePartage({ dossier_id, access_id: id }).unwrap().catch((error) => {
          console.error(`Erreur suppression accès ${id}:`, error);
          return { error, id };
        })
      );
      
      const results = await Promise.all(deletePromises);
      const errors = results.filter((r) => r && r.error);
      const successes = selectedRows.length - errors.length;

      if (successes > 0) {
        toast.success(`${successes} accès supprimé(s) avec succès.`);
      }
      if (errors.length > 0) {
        toast.error(`${errors.length} accès n'ont pas pu être supprimés.`);
      }

      setSelectedRows([]);
      setShowDeleteModal(false);
      if (editingAccess && selectedRows.includes(editingAccess.id)) {
        setEditingAccess(null);
      }
      refetch();
    } catch (err) {
      toast.error("Erreur lors de la suppression des accès.");
      setShowDeleteModal(false);
    }
  };

  const getRoleLabel = (r) => {
    switch (r) {
      case "LECTEUR":
        return "Lecteur (Lecture seule)";
      case "COMMERCIAL":
        return "Commercial (Facturation & Ventes)";
      case "EDITEUR":
        return "Éditeur (Comptable)";
      case "ADMIN":
        return "Administrateur";
      default:
        return r;
    }
  };

  const columns = [
    {
      dataField: "name",
      text: "Nom & Prénom",
      formatter: (cell, row) => `${row.first_name || ""} ${row.last_name || ""}`.trim() || "Invité",
    },
    {
      dataField: "email",
      text: "Email",
    },
    {
      dataField: "role",
      text: "Rôle",
      formatter: (cell) => {
        let bg = "#f3f4f6";
        let color = "#374151";
        if (cell === 'ADMIN') {
          bg = "#fee2e2";
          color = "#991b1b";
        } else if (cell === 'COMMERCIAL') {
          bg = "#e0f2fe";
          color = "#0369a1";
        } else if (cell === 'EDITEUR') {
          bg = "#e0e7ff";
          color = "#3730a3";
        }
        return (
          <span 
            className="px-2.5 py-1 rounded" 
            style={{ 
              backgroundColor: bg, 
              color: color, 
              fontSize: "12px", 
              fontWeight: "600",
              display: "inline-block"
            }}
          >
            {getRoleLabel(cell)}
          </span>
        );
      },
    },
    {
      dataField: "created_at",
      text: "Créé le",
      formatter: (cell) => formatDate(cell),
    },
  ];

  const nom = (
    <div style={{ display: 'flex' }}>
      <h4 className="titre-dossier">{"Partage de dossier"}</h4>
      {isAuthorized && <span className="nbrDossier">{filteredShares?.length}</span>}
    </div>
  );
  
  const description = <div>{"Gérez les autorisations et partages d'accès pour ce dossier"}</div>;

  if (isDossierLoading) {
    return (
      <div className="global d-flex justify-content-center align-items-center" style={{ backgroundColor: 'white', minHeight: '500px', borderRadius: '20px', padding: '20px' }}>
        <Spinner animation="border" style={{ color: '#009688' }} />
      </div>
    );
  }

  if (!isAuthorized) {
    return (
      <div
        className="global text-center py-5"
        style={{
          backgroundColor: 'white',
          minHeight: '500px',
          borderRadius: '20px',
          padding: '40px',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          fontFamily: 'Montserrat'
        }}
      >
        <RiLockPasswordLine size={64} style={{ color: '#ff6b6b', marginBottom: '20px' }} />
        <h3 className="fw-bold mb-3" style={{ color: '#1f2937' }}>{"Accès non autorisé"}</h3>
        <p className="text-muted" style={{ fontSize: '15px', maxWidth: '450px' }}>
          {"Vous devez être le propriétaire ou avoir le rôle d'administrateur pour pouvoir gérer les partages d'accès sur ce dossier d'entreprise."}
        </p>
      </div>
    );
  }

  return (
    <div
      className="global"
      style={{
        backgroundColor: 'white',
        minHeight: '500px',
        borderRadius: '20px',
        padding: '20px',
        fontFamily: 'Montserrat'
      }}
    >
      {!embedded && (
        <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 pb-3">
          <HeaderFirst nom={nom} description={description} hideBack={hideBack} />
        </div>
      )}

      <Row className={embedded ? "" : "mt-3"}>
        {/* Colonne Formulaire de Partage */}
        <Col lg={4} md={12} className="mb-4">
          <Card className="border shadow-sm rounded-4 bg-white p-2">
            <Card.Body>
              <Card.Title className="fw-bold mb-4" style={{ fontSize: "16px", color: '#009688' }}>
                {editingAccess ? "Modifier l'accès" : "Ajouter un accès"}
              </Card.Title>

              <Form onSubmit={handleSubmit}>
                <Form.Group className="mb-3">
                  <Form.Label className="fw-semibold text-muted" style={{ fontSize: "12px" }}>
                    {"Adresse Email *"}
                  </Form.Label>
                  <Form.Control
                    type="email"
                    required
                    disabled={!!editingAccess}
                    placeholder="email@example.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className={`rounded-pill py-2 px-3 border shadow-none ${editingAccess ? "bg-light" : ""}`}
                    style={{ fontSize: "14px" }}
                  />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label className="fw-semibold text-muted" style={{ fontSize: "12px" }}>
                    {"Prénom (optionnel)"}
                  </Form.Label>
                  <Form.Control
                    type="text"
                    disabled={!!editingAccess}
                    placeholder="Prénom de l'utilisateur"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    className={`rounded-pill py-2 px-3 border shadow-none ${editingAccess ? "bg-light" : ""}`}
                    style={{ fontSize: "14px" }}
                  />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label className="fw-semibold text-muted" style={{ fontSize: "12px" }}>
                    {"Nom (optionnel)"}
                  </Form.Label>
                  <Form.Control
                    type="text"
                    disabled={!!editingAccess}
                    placeholder="Nom de l'utilisateur"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    className={`rounded-pill py-2 px-3 border shadow-none ${editingAccess ? "bg-light" : ""}`}
                    style={{ fontSize: "14px" }}
                  />
                </Form.Group>

                <Form.Group className="mb-4">
                  <Form.Label className="fw-semibold text-muted" style={{ fontSize: "12px" }}>
                    {"Rôle / Permissions"}
                  </Form.Label>
                  <Form.Select
                    value={role}
                    onChange={(e) => setRole(e.target.value)}
                    className="rounded-pill py-2 px-3 border shadow-none"
                    style={{ fontSize: "14px" }}
                  >
                    <option value="COMMERCIAL">Commercial (Ventes, Factures, Clients)</option>
                    <option value="EDITEUR">Éditeur (Comptable)</option>
                    <option value="ADMIN">Administrateur</option>
                    <option value="LECTEUR">Lecteur (Lecture seule)</option>
                  </Form.Select>
                </Form.Group>

                <button
                  type="submit"
                  disabled={isAdding || isUpdating}
                  className="w-100 rounded-pill fw-semibold d-flex align-items-center justify-content-center border-0 text-white"
                  style={{ backgroundColor: '#009688', cursor: 'pointer', padding: "12px 24px" }}
                >
                  {(isAdding || isUpdating) ? (
                    <Spinner animation="border" size="sm" className="me-2" />
                  ) : (
                    !editingAccess && <RiAddLine className="me-1" size={18} />
                  )}
                  {editingAccess ? "Mettre à jour" : "Partager le dossier"}
                </button>

                {editingAccess && (
                  <button
                    type="button"
                    onClick={() => {
                      setEditingAccess(null);
                      setSelectedRows([]);
                    }}
                    className="w-100 rounded-pill fw-semibold border bg-white mt-2"
                    style={{ cursor: "pointer", fontSize: "13px", padding: "12px 24px" }}
                  >
                    {"Annuler la modification"}
                  </button>
                )}
              </Form>

              <div className="mt-4 p-3 bg-light rounded-3 d-flex align-items-start border">
                <RiInformationLine className="me-2 mt-0.5" style={{ color: '#009688' }} size={18} />
                <span className="text-muted" style={{ fontSize: "11px", lineHeight: "1.5" }}>
                  {"Si l'utilisateur invité n'a pas encore de compte, le système créera un compte invité temporaire et lui enverra les identifiants d'accès par email."}
                </span>
              </div>
            </Card.Body>
          </Card>
        </Col>

        {/* Colonne Liste des Accès existants */}
        <Col lg={8} md={12} className="mb-4">
          <Card className="border shadow-sm rounded-4 bg-white p-2">
            <Card.Body>
              <Card.Title className="fw-bold mb-4" style={{ fontSize: "16px", color: '#009688' }}>
                {"Utilisateurs ayant accès"}
              </Card.Title>

              {isSharesLoading ? (
                <div className="d-flex justify-content-center py-5">
                  <Spinner animation="border" style={{ color: '#009688' }} />
                </div>
              ) : shares.length === 0 ? (
                <div className="text-center py-5 text-muted">
                  <p className="mb-0">{"Ce dossier n'est partagé avec aucun autre utilisateur pour le moment."}</p>
                </div>
              ) : (
                <>
                  <hr style={{ margin: "15px 0 0 0" }} />
                  <div className="d-flex align-items-center justify-content-between py-2">
                    {/* Actions de sélection */}
                    <div className="d-flex align-items-center gap-3" style={{ height: "30px" }}>
                      {/* Bouton modifier */}
                      <button
                        onClick={onClickEdit}
                        title="Modifier l'accès"
                        style={{
                          cursor: "pointer",
                          border: "none",
                          background: "none",
                          padding: 0,
                          opacity: selectedRows.length === 1 ? 1 : 0.5,
                          pointerEvents: selectedRows.length === 1 ? "auto" : "none",
                          color: "#6b7280"
                        }}
                      >
                        <FiEdit size={18} />
                      </button>

                      {/* Bouton supprimer */}
                      <button
                        onClick={onClickDeleteBulk}
                        title="Supprimer l'accès"
                        style={{
                          cursor: "pointer",
                          border: "none",
                          background: "none",
                          padding: 0,
                          opacity: selectedRows.length > 0 ? 1 : 0.5,
                          pointerEvents: selectedRows.length > 0 ? "auto" : "none"
                        }}
                      >
                        <img src={supprimerIcon} alt="Supprimer" style={{ width: "18px", height: "18px" }} />
                      </button>
                    </div>

                    {/* Recherche */}
                    <div className="d-flex align-items-center">
                      <img src={searchIcon} alt="Rechercher" style={{ width: "16px", height: "16px" }} />
                      <input
                        type="text"
                        className="lineSeearch ps-2 py-2"
                        placeholder="Rechercher un accès..."
                        style={{
                          fontFamily: "Montserrat",
                          fontWeight: "600",
                          fontSize: "13px",
                          border: "none",
                          outline: "none",
                        }}
                        value={searchTerm}
                        onChange={(e) => {
                          setSearchTerm(e.target.value);
                          setPaginationIndex(0);
                        }}
                      />
                    </div>
                  </div>
                  <hr style={{ margin: "0 0 20px 0" }} />

                  <div className="table-responsive-sm">
                    <CustomBootstrapTable
                      keyField="id"
                      data={currentPageData}
                      columns={columns}
                      selectedRows={selectedRows}
                      onSelectRow={handleRowSelect}
                    />
                  </div>

                  {filteredShares.length > 0 && (
                    <div className="mt-4">
                      <TablePaginators
                        paginationIndex={paginationIndex}
                        setPaginationIndex={handlePageChange}
                        dataLength={filteredShares.length}
                        pageSize={pageSize}
                        setPageSize={setPageSize}
                        buttonHide="d-none"
                      />
                    </div>
                  )}
                </>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Modal Deletion Confirmation */}
      <ConfirmDeleteModal
        show={showDeleteModal}
        onHide={() => {
          setShowDeleteModal(false);
        }}
        onConfirm={handleConfirmDelete}
        title="Supprimer l'accès"
        message={`Êtes-vous sûr de vouloir supprimer les ${selectedRows.length} accès sélectionnés ? Cette action est irréversible.`}
        isLoading={isDeleting}
      />
    </div>
  );
}
